import {
  Box,
  Tooltip,
  TextField,
  Typography,
  TextFieldProps,
  FormHelperText,
} from "@mui/material";
import { useField } from "formik";
import InfoIcon from "@mui/icons-material/Info";
import { FC, memo, useEffect, useState } from "react";

import styles from "./styles";
import { InputProps } from "./type";
import PasswordIcon from "./password";
import DisplayError from "./displayError"; // Assuming this is a custom component that formats error messages
import { IMAGES } from "../../../common/constants";
import { blurHandler, focusHandler, pasteHandler } from "./helper";

const Input: FC<InputProps & TextFieldProps> = ({
  name,
  label,
  placeholder,
  externalStyles = {},
  isRequired = false,
  isPassword = false,
  isTooltip = false,
  isNumeric = false,
  labelTooltip,
  startAdornment,
  onBlur: customOnBlur,
  ...otherProps
}) => {
  const [field, meta, helpers] = useField(name);
  const [focus, setFocus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Check if the field has an initial error on mount
    if (meta.error && meta.error !== "" && !meta.touched && field.value) {
      helpers.setTouched(true); // Mark the field as touched
    }
    // eslint-disable-next-line
  }, [meta.error]); // Run only on mount

  const PasswordDetailsList = () => {
    return (
      <Box className="customTooltip">
        <Typography component="p" sx={styles.paratext}>
          Password Criteria:
        </Typography>
        <ul>
          <li>Character length between 8-16 characters</li>
          <li>1 Uppercase letter between A-Z</li>
          <li>1 Lowercase letter between a-z</li>
          <li>1 number between 0-9</li>
          <li>Special characters @, %, &, %. *, # only</li>
        </ul>
      </Box>
    );
  };

  const attributes: TextFieldProps = {
    ...field,
    ...otherProps,
    placeholder,
    error: meta.touched && Boolean(meta.error),
    sx: [
      styles.authInput,
      meta.value && Boolean(!meta.error) ? styles.filled : styles.notFilled,
    ],
    autoComplete: "off",
    onBlur: (evt: any) => {
      blurHandler(evt, field, setFocus);
      customOnBlur?.(evt);
    },
    onFocus: () => focusHandler(setFocus),
    onPaste: (evt: any) => pasteHandler(evt, name, isNumeric),
    type: isPassword && !showPassword ? "password" : "text",
  };

  if (isPassword) {
    attributes.InputProps = {
      endAdornment: (
        <PasswordIcon
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          value={field.value}
        >
          {attributes.error && !focus}
        </PasswordIcon>
      ),
    };
  }

  return (
    <Box sx={[externalStyles]}>
      <Box
        sx={{ ...styles.labelIcon, alignItems: "center", marginBottom: "10px" }}
      >
        {label && (
          <Typography
            component="div"
            variant="h6"
            color="primary"
            sx={{ ...styles.label, marginBottom: 0 }}
          >
            {`${label}${isRequired ? "*" : ""}`}
          </Typography>
        )}
        {labelTooltip && (
          <Tooltip arrow placement="top" title={labelTooltip}>
            <InfoIcon
              className="labelInfoIcon"
              style={{
                cursor: "pointer",
                color: "#e5063e",
                fontSize: "18px",
                marginLeft: "5px",
                marginTop: "-2px",
              }}
            />
          </Tooltip>
        )}
        {isTooltip && (
          <Tooltip
            placement="left-end"
            enterTouchDelay={0}
            title={<PasswordDetailsList />}
          >
            <Box
              component="img"
              src={IMAGES.infoIcon}
              alt="info_logo"
              sx={styles.infoIcon}
            />
          </Tooltip>
        )}
      </Box>
      <TextField {...attributes} />
      {meta.touched && Boolean(meta.error) && (
        <FormHelperText error sx={styles.errorTooltip}>
          <DisplayError meta={meta} />
        </FormHelperText>
      )}
    </Box>
  );
};

export default memo(Input);
